
import React from "react";
import { BoolHelper, Classname, useSearchParams } from "@rivtn/noseur";
import { i18nManager } from "../i18n";
import MatchRow from "./common/MatchRow";
import Pagination from "./common/Pagination";
import { Utils } from "../../../../utils";
import PageHead from "./common/PageHead";
import styles from "../assets/style.module.css";
import { ApiResponseData, BaseService, IProfileWebsite, ITournamentEditionMatch, OmneHostService, SanitizedResponse } from "../../../../services";

export interface ScheduleProps {
    publicId: string;
    website: IProfileWebsite;
}

function Schedule({ website, publicId }: ScheduleProps) {

    const labels = i18nManager.Labels.schedule;
    const settings = website.schedule_settings ?? {} as IProfileWebsite["schedule_settings"];
    const omnehostService = OmneHostService.getInstance();
    const [matches, setMatches] = React.useState<ApiResponseData<ITournamentEditionMatch>>();
    const { page, setPage, status, setStatus, tipe, setTipe } = useSearchParams({ defaultInit: window.location.search, params: { page: 1, status: "", tipe: "" } });

    const typeTabs = [["", labels.personal_and_teams], ['"MEMBER"', labels.personal], ['"TEAM"', labels.teams]].filter((_, i) => {
        if (BoolHelper.equalsAny(i, [0, 2]) && !settings.show_past_team_matches && !settings.show_ongoing_upcoming_team_matches) return false;
        if (BoolHelper.equalsAny(i, [0, 1]) && !settings.show_past_personal_matches && !settings.show_ongoing_upcoming_personal_matches) return false;
        return true;
    });
    const statusTabs = [["", labels.all], ["UPCOMING", labels.upcoming], ["ONGOING", labels.live], ["ENDED", labels.past]].filter(([s]) => {
        if (s === "" && (!settings.show_past_team_matches || !settings.show_past_personal_matches
            || !settings.show_ongoing_upcoming_team_matches || !settings.show_ongoing_upcoming_personal_matches)) return false;
        if (s === "ENDED" && ((tipe === '"TEAM"' && !settings.show_past_team_matches) || (tipe === '"MEMBER"' && !settings.show_past_personal_matches))) return false;
        if (BoolHelper.equalsAny(s, ["UPCOMING", "ONGOING"]) && ((tipe === '"TEAM"' && !settings.show_ongoing_upcoming_team_matches) || (tipe === '"MEMBER"' && !settings.show_ongoing_upcoming_personal_matches))) return false;
        return true;
    });

    React.useEffect(fetchInformation, [omnehostService, page, status, tipe]);

    return (<>
        <PageHead kicker={labels.kicker} title={labels.title} titleEm={labels.title_em} subtitle={labels.desc} />
        <section className={styles["section"]}>
        <div className={styles["container"]}>
            <div className={styles["tabs"]}>
                {typeTabs.map(([v, l]) => (<button key={v} className={Classname.build(styles["tab"], { [styles["active"]]: tipe === v })} onClick={() => setTipe(v)}>{l}</button>))}
            </div>
            <div className={styles["tabs"]} style={{ marginBottom: 32 }}>
                {statusTabs.map(([v, l]) => (<button key={v} className={Classname.build(styles["tab"], { [styles["active"]]: status === v })} onClick={() => setStatus(v)}>{l}</button>))}
            </div>

            {(!!matches?.content)
                ? ((matches.content.length > 0)
                    ? (<div className={styles["matches"]}>
                        {matches.content.map((m) => (<MatchRow key={m.external_id} publicId={publicId} website={website} match={m} />))}
                    </div>)
                    : (Utils.emptyState()))
                : (Utils.loadingState())}

            <Pagination page={parseInt("" + page)} rowsPerPage={matches?.size} totalRecords={matches?.total_elements} onPageChange={setPage} />
        </div>
        </section>
    </>);

    function fetchInformation() {
        setMatches({});
        omnehostService.queryMemberSchedule(website.member.username, {
            page,
            size: 20,
            status__eq: status,
            extra_data__like: tipe,
            sort: ["start_date,ASC", "status,ASC"],
        }).then(({ apiResponse }: SanitizedResponse<ITournamentEditionMatch>) => {
            setMatches(apiResponse.data!);
        }).catch(BaseService.reportError);
    }
}

export default Schedule;
